import { Graphics } from "pixi.js"
import { DISPLAY_FONT_FAMILY } from "../text/typography"

/**
 * Shared look of the login screen: one dark, translucent card over the intro art.
 *
 * Every surface here is drawn with plain fills and strokes -- no pixi filters. A filter allocates a
 * render texture per object and is the first thing to cost us frames on a weak GPU, so a "shadow"
 * is an offset silhouette drawn behind the shape and a "glow" is a fatter, low-alpha copy.
 */

/**
 * The intro's type. Jost is bundled from src/fonts (OFL, see OFL.txt beside it) and declared in
 * index.html; Verdana is the rest of the GUI's face and the fallback while the woff2 is in flight.
 */
export const INTRO_FONT = DISPLAY_FONT_FAMILY

/**
 * The logical screen the GUI draws into. The renderer is 800x600 with `stage.scale.y = SCALER`
 * (1.3), so usable space is 800 wide by 600/1.3 tall -- which is what BOTTOM (460) in gui_manager
 * already encodes. Centering against `GuiManager.screenDimensions()` does NOT work: it returns
 * `stage.width/height`, i.e. the bounding box of whatever happens to be on the stage right then.
 */
export const SCREEN_W = 800
export const SCREEN_H = 460

/**
 * Firelight, not forest. The loading screen before this one is amber and gold on near-black, and the
 * game after it is a camp around a fire, so the login screen speaks the same colours: dark smoked
 * glass, a bronze rim, an amber call to action, and a wordmark that ignites amber, flares gold and
 * cools to the loading screen's cream. (It was a slate card with a lime button, which read as a web
 * app's sign-in over a screenshot of the game.)
 */
export const COLORS = {
    panelFill: 0x120d0a,
    panelRim: 0x8a6a48,
    fieldFill: 0x090605,
    fieldRim: 0x8a6a48,
    text: 0xfbefdc,
    muted: 0xb9a68c,
    accent: 0xf2a94e,
    accentHi: 0xffd166,
    accentDim: 0xc98a3a,
    onAccent: 0x2b1a0b,
    danger: 0xff9f86,
    shadow: 0x000000,
    scrim: 0x0b0806,
    highlight: 0xffedc7,
}

export const METRICS = {
    cardWidth: 460,
    cardPadding: 26,
    cardRadius: 10,
    fieldHeight: 44,
    fieldRadius: 8,
    buttonHeight: 44,
    buttonRadius: 8,
    gutter: 16,
}

/** Content width inside the card. */
export const CONTENT_W = METRICS.cardWidth - METRICS.cardPadding * 2

/**
 * A soft shadow all the way round the card, PANEL_HALO_WIDTH logical pixels wide: one ring per pixel
 * outward, darkest against the card's edge and easing out to nothing.
 */
const PANEL_HALO_WIDTH = 16
const PANEL_HALO_EDGE = 0.45
const PANEL_HALO = Array.from({ length: PANEL_HALO_WIDTH }, (_, i) => PANEL_HALO_EDGE * (1 - (i + 0.5) / PANEL_HALO_WIDTH) ** 2)

/**
 * The card surface: an offset dark silhouette for depth, a soft shadow all round it, then the
 * translucent fill and its rim. All drawn onto `g`, which is cleared first, so this is safe to call
 * on every repaint.
 */
export const drawPanel = (g: Graphics, w: number, h: number, radius: number = METRICS.cardRadius) => {
    g.clear()

    g.beginFill(COLORS.shadow, 0.34)
    g.drawRoundedRect(3, 7, w, h, radius)
    g.endFill()

    // Rings rather than stacked slabs, so none of it darkens the translucent glass of the card itself.
    PANEL_HALO.forEach((alpha, i) => {
        const inner = i
        const outer = i + 1
        g.beginFill(COLORS.shadow, alpha)
        g.drawRoundedRect(-outer, -outer, w + outer * 2, h + outer * 2, radius + outer)
        g.beginHole()
        g.drawRoundedRect(-inner, -inner, w + inner * 2, h + inner * 2, radius + inner)
        g.endHole()
        g.endFill()
    })

    g.lineStyle({ width: 1, color: COLORS.panelRim, alpha: 0.6 })
    g.beginFill(COLORS.panelFill, 0.88)
    g.drawRoundedRect(0, 0, w, h, radius)
    g.endFill()
    g.lineStyle(0)

    // Light catching the top edge: one hairline, inset past the corners, so the slab reads as glass
    // with a lip rather than a flat cut-out.
    g.beginFill(COLORS.highlight, 0.12)
    g.drawRect(radius, 1, w - radius * 2, 1)
    g.endFill()
}

/** A small chip of the same material, for the socials row and the fullscreen toggle. */
export const drawChip = (g: Graphics, w: number, h: number, radius: number = 8) => {
    g.clear()
    g.lineStyle({ width: 1, color: COLORS.panelRim, alpha: 0.35 })
    g.beginFill(COLORS.panelFill, 0.8)
    g.drawRoundedRect(0, 0, w, h, radius)
    g.endFill()
    g.lineStyle(0)
}

export type FieldTone = "default" | "focused" | "error"

/**
 * The frame behind a text field. Handed to pixi-text-input as its `box` generator, which calls it
 * with the DOM input's measured size and one of DEFAULT / FOCUSED / DISABLED -- so the library
 * repaints the rim on focus for free. `error` is ours, applied by redrawing the cached boxes.
 */
export const drawFieldBox = (g: Graphics, w: number, h: number, tone: FieldTone) => {
    g.clear()

    const rim =
        tone === "error"
            ? { color: COLORS.danger, alpha: 0.9, width: 2 }
            : tone === "focused"
              ? { color: COLORS.accent, alpha: 0.85, width: 2 }
              : { color: COLORS.fieldRim, alpha: 0.4, width: 1 }

    g.lineStyle({ width: rim.width, color: rim.color, alpha: rim.alpha })
    g.beginFill(COLORS.fieldFill, 0.72)
    g.drawRoundedRect(0, 0, w, h, METRICS.fieldRadius)
    g.endFill()
    g.lineStyle(0)
}

export const introFieldBoxGenerator = (tone: () => FieldTone) => (w: number, h: number, state: string) => {
    const g = new Graphics()
    const override = tone()
    drawFieldBox(g, w, h, override === "error" ? "error" : state === "FOCUSED" ? "focused" : "default")
    return g
}

/**
 * Jost is a webfont, and a pixi Text rasterizes ONCE with whatever face is resolved at build time --
 * it is not re-rasterized when the font later arrives. So the intro waits for the face before it
 * draws anything. Resolves either way: a failed font load costs us Verdana, not the login screen.
 */
export const ensureIntroFont = (): Promise<void> => {
    const fonts: any = typeof document !== "undefined" ? (document as any).fonts : undefined
    if (!fonts || typeof fonts.load !== "function") {
        return Promise.resolve()
    }

    return Promise.all([fonts.load('300 88px "Jost"'), fonts.load('400 16px "Jost"'), fonts.load('500 16px "Jost"')])
        .then(() => undefined)
        .catch(() => undefined)
}
